Ext.define('SA.data.TimeSeriesTableView', {
    alias: 'view.timeSeries',
    extend: 'SA.data.ReportView',

    mixins: {
        filterable: 'SA.data.mixin.Filterable',
        locations: 'SA.data.mixin.ManyLocations'
    },

    statics: {
        create({project, censusRelease, name, locationItems, attributes}) {
            var data = {
                type: 'timeSeries',
                attribute: attributes.filter(id => this.supportsAttribute(id))[0] || null,
                buildingBlocksCensusRelease: censusRelease,
                filterVisibility: 'strikeout',
                locationItems,
                name
            };

            return new SA.data.TimeSeriesTableView({project, data});
        },

        supportsAttribute(attribute) {
            return SA.data.AttributesCache.isPolygonAttribute(attribute);
        }
    },

    constructor() {
        this.callParent(arguments);

        this.includeProjections = true;
        this.transposed = false;

        this.getSort();
    },

    addAttributeImpl(attribute) {
        this.data.attribute = attribute;

        this.seriesAttributes = null;

        this.project.addAttribute(attribute);

        this.auditFilter();
    },

    addLocationImpl(location) {
        this.mixins.locations.addLocationImpl.call(this, location);

        this.project.addLocation(location);
    },

    createController() {
        return new SA.TimeSeriesController(this);
    },

    createDataMap() {
        return new SA.data.DataMap({
            country: this.getCountry()
        });
    },

    getAttribute() {
        return this.data.attribute;
    },

    getAttributes() {
        var attribute = this.getAttribute();

        return attribute ? [attribute] : [];
    },

    async getExportQuery() {
        var me = this,
            attributes = await getAttributes(),
            locations = this.getQueryLocations(),
            filter = this.getFilterQuery(),
            filterVisibility = this.getFilterVisibility(),
            query = this.getDataMap().createQueryConfig(attributes, locations);

        if (filter && filterVisibility === 'hide') {
            query.filter = filter;
        }

        query.fields = getFields();
        query.sort = [['desc', 'locationSeries'].concat(Ext.Array.pluck(locations, 'id'))];

        return query;

        async function getAttributes() {
            var attributes = await me.getSeriesAttributesPromise(),
                sortByYear = (a, b) => a.year - b.year;

            return attributes.sort(sortByYear).map(({attribute}) => attribute);
        }

        function getFields() {
            var baseColumns = ['name', 'geographicUnitName'];

            return SA.Exports.getColumnExpressions({attributes, baseColumns, filter, filterVisibility});
        }
    },

    getIncludeProjections() {
        return this.includeProjections;
    },

    getReferencedAttributes() {
        var seriesAttributes = Ext.Array.from(this.seriesAttributes).map(attribute => attribute.attribute);

        return [this.getAttribute(), ...seriesAttributes];
    },

    getReportData(cfg = {}) {
        var {fn, scope} = cfg,
            map = this.getDataMap(),
            filter = this.getFilterQuery(),
            derived;

        if (filter && this.getFilterVisibility() !== 'disable') {
            derived = this.getStrikeoutFilterDerivedAttribute(filter);
        }

        map.ensure({
            attributes: this.getSeriesAttributes(fn, scope).map(({attribute}) => attribute),
            derivedAttributes: derived,
            locations: this.getQueryLocations(),

            fn,
            scope
        });

        return map.getData();
    },

    getSeriesAttributes(fn, scope) {
        var me = this,
            attributeId = this.getAttribute(),
            isUserAttribute = SA.data.UserAttributes.isUserAttribute(attributeId),
            query;

        if (!attributeId) {
            return [];
        }

        if (this.seriesAttributes) {
            return maybeFilterProjections(this.seriesAttributes);
        }

        query = SA.data.Query.from({
            fields: ['attributeSeries'],
            limit: 1,
            type: 'attributes',
            userData: isUserAttribute,
            where: ['=', 'attribute', attributeId]
        });

        query.addSuccess(response => {
            var query = SA.data.Query.from({
                fields: SA.data.AttributesCache.getFields(),
                limit: 1000,
                type: 'attributes',
                userData: isUserAttribute,

                where: SA.data.AttributesQuery.getCountryAndStatusVisiblePredicate(this.getCountry()).concat([
                    ['=', 'attributeSeries', response.results[0].attributeSeries]
                ])
            });

            query.addSuccess(response => {
                SA.data.AttributesCache.purgeUnreferenced();
                SA.data.AttributesCache.add(Ext.clone(response.results));

                this.seriesAttributes = response.results.map(mapResultItem).sort(sortByYear);

                Ext.callback(fn, scope);

                function mapResultItem({attribute, projected, year}) {
                    return {
                        attribute,
                        projected: projected === 'true',
                        year
                    };
                }

                function sortByYear(attribute1, attribute2) {
                    var year1 = attribute1.year,
                        year2 = attribute2.year;

                    if (!year1) {
                        return 1;
                    }
                    else if (!year2) {
                        return -1;
                    }

                    return year1 - year2;
                }
            });

            query.run();
        });

        query.run();

        return [];

        function maybeFilterProjections(attributes) {
            if (!me.includeProjections) {
                return attributes.filter(({projected}) => !projected);
            }

            return attributes;
        }
    },

    getSeriesAttributesPromise() {
        return new Promise(resolve => {
            var me = this,
                attributes = this.getSeriesAttributes(onHasIds);

            if (attributes.length) {
                onHasIds();
            }

            async function onHasIds() {
                var ids = me.getSeriesAttributes().map(attribute => attribute.attribute),
                    attributes = await SA.data.AttributesCache.getPromise(ids);

                resolve(attributes);
            }
        });
    },

    getSort() {
        var data = this.data;

        if (!data.sort) {
            this.setSort(null);
        }

        return [data.sort[0], SA.data.LocationsCache.toLocationSeries(data.sort[1])];
    },

    getSortDirection() {
        return this.getSort()[0];
    },

    getSortLocation() {
        return this.getSort()[1];
    },

    isSavable() {
        return this.getAttribute() && this.getLocations().length;
    },

    isTransposed() {
        return this.transposed;
    },

    onCustomLocationUpdateImpl(location) {
        this.getDataMap().removeLocation(SA.data.LocationsCache.normalizeLocation(location));
    },

    removeAttributeImpl(attribute) {
        if (attribute === this.data.attribute) {
            this.data.attribute = null;
        }

        this.callParent([attribute]);

        this.auditFilter();
    },

    removeLocationImpl(location) {
        this.callParent([location]);

        if (SA.data.LocationsCache.toLocationSeries(location) === this.getSortLocation()) {
            this.setSort(null);
        }
    },

    reverseSort() {
        var sort = this.getSort(),
            sortProperty = sort[1],
            sortDirection = sort[0] === 'desc' ? 'asc' : 'desc';

        this.setSort(sortProperty, sortDirection);
    },

    setFilterImpl(filter) {
        var changed = this.mixins.filterable.setFilterImpl.call(this, filter);

        if (changed) {
            this.getDataMap().removeAttribute('filter');
        }

        return changed;
    },

    setIncludeProjections(value) {
        if (value === this.includeProjections) {
            return;
        }

        this.includeProjections = value;

        this.fireEvent('change:includeProjections');
    },

    setSort(location, direction) {
        var oldSort = this.data.sort,
            toLocationSeries = SA.data.LocationsCache.toLocationSeries,
            sort;

        if (!location) {
            location = null;
        }
        else if (Array.isArray(location)) {
            direction = location[0];
            location = location[1];
        }



                if (!direction) {
            if (oldSort && toLocationSeries(oldSort[1]) === toLocationSeries(location)) {
                return;
            }

            direction = location ? 'desc' : 'asc';
        }

        sort = [direction, SA.data.LocationsCache.toLocationItem(location)];

        if (SA.Json.differ(sort, oldSort)) {
            this.setWithEvent('sort', sort);
        }
    },

    setTransposed(transposed) {
        if (transposed !== this.transposed) {
            this.transposed = transposed;

            this.fireEvent('change:transposed');
        }
    },

    toggleTransposed() {
        this.setTransposed(!this.isTransposed());
    }
});